import { zahlAus } from './Zahlenfeld.js'

/**
 * Menge, Preis und Rabatt einer Position – geprüft, bevor sie hinausgehen.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was vorher geschah
 * ════════════════════════════════════════════════════════════════════════════
 *
 * In der Belegansicht stand `Number(preis.replace(',', '.')) || 0`. Wer
 * «1'250.50» schrieb – so, wie man in der Schweiz Franken schreibt –, bekam
 * `NaN`, daraus wurde 0, und der Server nahm 0.00 an: Preise von null sind
 * erlaubt. Die Position stand danach still auf CHF 0.00, und niemand hatte
 * etwas falsch gemacht ausser der Maske.
 *
 * Jetzt liest `zahlAus` – dieselbe Funktion wie im Zahlenfeld der
 * Express-Erfassung, sie kennt das Apostroph. Was sie nicht lesen kann, ist
 * ein Fehler am Feld und kein Wert. Eine Null ist nur dann eine Null, wenn
 * jemand sie hingeschrieben hat.
 *
 * Die Grenzen sind die des Servers (`lineSchema` in routes/documents.ts). Sie
 * stehen hier noch einmal, damit der Fehler am Feld erscheint und nicht als
 * «Ungültige Eingabe» nach dem Speichern. Der Server prüft weiter selbst.
 */

export type Positionsfeld = 'menge' | 'preis' | 'rabatt'

export interface Positionstexte {
  menge: string
  preis: string
  rabatt: string
}

export interface Positionswerte {
  quantity: number
  unitPriceRp: number
  discountPercent: number
}

export type Positionsbefund =
  | { ok: true; werte: Positionswerte }
  | { ok: false; fehler: Partial<Record<Positionsfeld, string>> }

const MAX_MENGE = 1_000_000
const MAX_PREIS_RP = 100_000_000

/** Mehr als zwei Nachkommastellen gibt es bei Franken nicht. */
function nachkommastellen(text: string): number {
  const roh = text.replace(/’|'/g, '').replace(',', '.').trim()
  const punkt = roh.indexOf('.')
  return punkt === -1 ? 0 : roh.length - punkt - 1
}

export function pruefePosition(t: Positionstexte): Positionsbefund {
  const fehler: Partial<Record<Positionsfeld, string>> = {}

  const menge = zahlAus(t.menge)
  if (t.menge.trim() === '') fehler.menge = 'Bitte eine Menge eintragen – auch 0 ist möglich.'
  else if (menge === null) fehler.menge = `«${t.menge.trim()}» ist keine Zahl.`
  else if (menge < 0) fehler.menge = 'Die Menge darf nicht negativ sein.'
  else if (menge > MAX_MENGE) fehler.menge = 'Die Menge ist zu gross.'

  const preis = zahlAus(t.preis)
  if (t.preis.trim() === '') fehler.preis = 'Bitte einen Preis eintragen – auch 0.00 ist möglich.'
  else if (preis === null) fehler.preis = `«${t.preis.trim()}» ist kein Betrag.`
  else if (preis < 0) fehler.preis = 'Der Preis darf nicht negativ sein.'
  else if (nachkommastellen(t.preis) > 2) fehler.preis = 'Höchstens zwei Stellen nach dem Punkt.'
  else if (Math.round(preis * 100) > MAX_PREIS_RP) fehler.preis = 'Der Preis ist zu gross.'

  /* Ein leerer Rabatt ist kein Rabatt – das ist der häufigste Fall. */
  const rabatt = t.rabatt.trim() === '' ? 0 : zahlAus(t.rabatt)
  if (rabatt === null) fehler.rabatt = `«${t.rabatt.trim()}» ist keine Zahl.`
  else if (rabatt < 0 || rabatt > 100) fehler.rabatt = 'Der Rabatt liegt zwischen 0 und 100 %.'

  if (Object.keys(fehler).length > 0) return { ok: false, fehler }
  return {
    ok: true,
    werte: {
      quantity: menge as number,
      unitPriceRp: Math.round((preis as number) * 100),
      discountPercent: rabatt as number
    }
  }
}
